import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

interface VerificationModalState {
  isOpen: boolean;
  actionName: string;
  actionDescription: string;
  onVerifiedCallback?: () => void;
}

export const useEmailVerificationGuard = () => {
  const { emailVerified } = useAuth();
  const [modalState, setModalState] = useState<VerificationModalState>({
    isOpen: false,
    actionName: '',
    actionDescription: '',
    onVerifiedCallback: undefined
  });

  const requireVerification = useCallback((
    actionName: string,
    actionDescription: string,
    callback: () => void
  ) => {
    if (emailVerified) {
      // Email already verified, execute immediately
      callback();
      return;
    }

    // Show verification modal
    setModalState({
      isOpen: true,
      actionName,
      actionDescription,
      onVerifiedCallback: callback
    });
  }, [emailVerified]);

  const closeModal = useCallback(() => {
    setModalState({
      isOpen: false,
      actionName: '',
      actionDescription: '',
      onVerifiedCallback: undefined
    });
  }, []);

  const onVerificationComplete = useCallback(() => {
    if (modalState.onVerifiedCallback) {
      modalState.onVerifiedCallback();
    }
    closeModal();
  }, [modalState.onVerifiedCallback, closeModal]);

  return {
    requireVerification,
    modalState,
    closeModal,
    onVerificationComplete,
    isVerified: emailVerified
  };
};